/**
 * Early-access context.
 *
 * One source of truth for the limited-release gate on the frontend. Mounts
 * once at the app root; consumed via the `useEarlyAccess()` hook by:
 *
 *   - LoginPage / RegisterPage — to know whether to show the form or the
 *     "coming soon" placeholder
 *   - EarlyAccessExpiredPage / LinkExpiredPage — to render the correct
 *     redirect when the grant or invite is no longer valid
 *   - featureFlags.ts `useRegistrationOpen` hook — to merge the early-access
 *     state with the PostHog flag
 *
 * Lifecycle on mount:
 *   1. If `?early_access=...` is in the URL, POST to /api/early-access/redeem.
 *      On success → status is set to `active`. On failure → we navigate the
 *      user to `/link-expired` and clear the URL param either way.
 *   2. Regardless of redemption, GET /api/early-access/status to sync state
 *      with the backend. If the server reports `expired` or `revoked` AND
 *      the user was on a gated page, navigate them to /early-access-expired.
 *
 * Status is refreshed again whenever `refresh()` is called (e.g. after an
 * admin revokes their own grant, which is rare but possible).
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getEarlyAccessStatus,
  redeemEarlyAccessToken,
  type EarlyAccessStatus,
} from '../services/api/earlyAccessApi';
import { readEarlyAccessTokenFromUrl, stripEarlyAccessTokenFromUrl } from '../utils/earlyAccess';

/** Paths where the expiry page / link-expired page would make sense to auto-redirect to. */
const GATED_PATHS = new Set(['/login', '/register']);

export interface EarlyAccessContextValue {
  /** Null while we're still resolving on first mount; otherwise the latest server state. */
  status: EarlyAccessStatus | null;
  /** True once the first fetch (redeem + status) has completed. */
  ready: boolean;
  /** True when status.active is true. Convenience accessor for consumers. */
  isActive: boolean;
  /** Force a re-fetch of /api/early-access/status — used after in-app events. */
  refresh: () => Promise<void>;
}

const EarlyAccessContext = createContext<EarlyAccessContextValue | undefined>(undefined);

export function EarlyAccessProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [status, setStatus] = useState<EarlyAccessStatus | null>(null);
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const refresh = useCallback(async () => {
    const next = await getEarlyAccessStatus();
    setStatus(next);
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Safety timeout: if /status is slow (Railway cold start, transient
    // network, rate-limit retry) the fetch can take tens of seconds before
    // axios's 30s timeout fires. During that window `ready` stays false
    // and the gated pages render as a blank AuthPage shell — just the
    // back link, nothing else — which looks like the app is broken.
    //
    // We flip ready to true after READY_FALLBACK_MS regardless. The UI
    // falls back to the pre-decision state (not-active → Coming soon on
    // gated pages, feature flag alone on everything else), and when the
    // fetch eventually resolves setStatus() triggers a re-render with
    // the real state. Worst case the user briefly sees Coming soon before
    // it swaps to the login form; far better than staring at an empty
    // page for a full minute.
    const READY_FALLBACK_MS = 3000;
    const readyTimer = setTimeout(() => {
      if (!cancelled) setReady(true);
    }, READY_FALLBACK_MS);

    (async () => {
      const urlToken = readEarlyAccessTokenFromUrl();
      if (urlToken) {
        const result = await redeemEarlyAccessToken(urlToken);
        stripEarlyAccessTokenFromUrl();
        if (!cancelled && !result.ok) {
          // Redemption failed — send the user to the link-expired page and
          // stop here. Don't bother calling /status; they don't have a cookie.
          setStatus({ active: false, reason: 'no_cookie' });
          setReady(true);
          navigate('/link-expired', { replace: true });
          return;
        }
        // Success — fall through to the normal status fetch so the
        // returned object matches the /status shape exactly.
      }

      const next = await getEarlyAccessStatus();
      if (cancelled) return;
      setStatus(next);
      setReady(true);

      // If the user just loaded /login or /register and their grant is
      // expired or revoked, bounce them to the friendly expiry page. We
      // scope this to gated pages so a happy-path user landing on the
      // homepage while their grant happens to be expired doesn't get
      // yanked around.
      if (!next.active && (next.reason === 'expired' || next.reason === 'revoked')) {
        if (GATED_PATHS.has(location.pathname)) {
          navigate('/early-access-expired', { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(readyTimer);
    };
    // Intentionally only run once on mount. Route changes don't need to
    // re-trigger the redemption; status stays sticky until `refresh()`.
  }, []);

  const value = useMemo<EarlyAccessContextValue>(
    () => ({
      status,
      ready,
      isActive: status?.active === true,
      refresh,
    }),
    [status, ready, refresh],
  );

  return <EarlyAccessContext.Provider value={value}>{children}</EarlyAccessContext.Provider>;
}

export function useEarlyAccess(): EarlyAccessContextValue {
  const ctx = useContext(EarlyAccessContext);
  if (!ctx) {
    // Provide a safe default rather than throwing — a few bootstrap paths
    // (prerender, error boundary fallback) may render before the provider
    // mounts. The default is "not yet known, nothing active" which gates
    // pages to the coming-soon placeholder until state resolves.
    return {
      status: null,
      ready: false,
      isActive: false,
      refresh: async () => {
        /* no-op */
      },
    };
  }
  return ctx;
}
